import React, { useEffect } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { useAuthContext } from 'features/auth/context/AuthContext'
import { AccessibleTitle } from 'features/home/components/AccessibleTitle'
import { CategoryBlock as CategoryBlockData } from 'features/home/types'
import { analytics } from 'libs/analytics/provider'
import { ContentTypes } from 'libs/contentful/types'
import { useFeatureFlag } from 'libs/firebase/firestore/featureFlags/useFeatureFlag'
import { RemoteStoreFeatureFlags } from 'libs/firebase/firestore/types'
import { useLocation } from 'libs/location/LocationWrapper'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { AIFakeDoorModal } from 'shared/AIFakeDoorModal/AIFakeDoorModal'
import { CategoryButton } from 'shared/categoryButton/CategoryButton'
import { useModal } from 'ui/components/modals/useModal'
import { AIFakeDoorBanner } from 'ui/components/ModuleBanner/AIFakeDoorBanner'
import { getSpacing } from 'ui/theme'
import { colorMapping } from 'ui/theme/colorMapping'

type CategoryListProps = {
  id: string
  title: string
  categoryBlockList: CategoryBlockData[]
  index: number
  homeEntryId: string
}

const BLOCK_HEIGHT = getSpacing(25)
const DESKTOP_MAX_WIDTH = getSpacing(37.33)
const DESKTOP_MIN_WIDTH = 'none'
const MOBILE_MIN_WIDTH = '40%'
const MOBILE_MIN_WIDTH_WHEN_FONT_ZOOMED = '100%'
const MOBILE_MAX_WIDTH = '49%'

export const CategoryListModule = ({
  id,
  title,
  categoryBlockList,
  index,
  homeEntryId,
}: CategoryListProps) => {
  const mobileMinWidth = useMobileFontScaleToDisplay({
    default: MOBILE_MIN_WIDTH,
    at200PercentZoom: MOBILE_MIN_WIDTH_WHEN_FONT_ZOOMED,
  })

  const { designSystem } = useTheme()
  const enableAIFakeDoor = useFeatureFlag(RemoteStoreFeatureFlags.ENABLE_AI_FAKE_DOOR)
  const { visible, showModal, hideModal } = useModal(false)
  const { userLocation } = useLocation()
  const { user } = useAuthContext()

  useEffect(() => {
    void analytics.logModuleDisplayedOnHomepage({
      moduleId: id,
      moduleType: ContentTypes.CATEGORY_LIST,
      index,
      homeEntryId,
    })
  }, [id, homeEntryId, index])

  const onPressAIFakeDoorBanner = () => {
    void analytics.logHasClickedFakeDoorCTA({
      featureName: 'conversational_search_AI',
      from: 'home',
      homeEntryId,
    })
    showModal()
  }

  return (
    <React.Fragment>
      {enableAIFakeDoor ? (
        <BannerContainer>
          <AIFakeDoorBanner onPress={onPressAIFakeDoorBanner} />
        </BannerContainer>
      ) : null}
      <Container>
        <AccessibleTitle title={title} />
        <StyledView>
          {categoryBlockList.map((item) => {
            const fillFromDesignSystem =
              designSystem.color.background[colorMapping[item.color].fill ?? 'default']

            const borderFromDesignSystem =
              designSystem.color.border[colorMapping[item.color].border ?? 'default']
            return (
              <StyledCategoryButton
                key={item.id}
                label={item.title}
                height={BLOCK_HEIGHT}
                mobileMinWidth={mobileMinWidth}
                fillColor={fillFromDesignSystem || colorMapping[item.color].fill}
                borderColor={borderFromDesignSystem || colorMapping[item.color].border}
                onBeforeNavigate={() => {
                  void analytics.logCategoryBlockClicked({
                    moduleId: item.id,
                    moduleListID: id,
                    entryId: homeEntryId,
                    toEntryId: item.homeEntryId,
                  })
                }}
                navigateTo={{
                  screen: 'ThematicHome',
                  params: {
                    homeId: item.homeEntryId,
                    from: 'category_block',
                    moduleId: item.id,
                    moduleListId: id,
                  },
                }}
              />
            )
          })}
        </StyledView>
      </Container>
      {enableAIFakeDoor ? (
        <AIFakeDoorModal
          close={hideModal}
          visible={visible}
          userLocation={userLocation}
          userCity={user?.city}
        />
      ) : null}
    </React.Fragment>
  )
}

const StyledView = styled.View(({ theme }) => {
  const MOBILE_GAPS_AND_PADDINGS = theme.designSystem.size.spacing.s
  const DESKTOP_GAPS_AND_PADDINGS = theme.designSystem.size.spacing.l
  return {
    flexDirection: 'row',
    width: '100%',
    paddingHorizontal: theme.contentPage.marginHorizontal,
    ...(theme.isMobileViewport
      ? {
          flexWrap: 'wrap',
          gap: MOBILE_GAPS_AND_PADDINGS,
          paddingVertical: MOBILE_GAPS_AND_PADDINGS,
        }
      : {
          gap: DESKTOP_GAPS_AND_PADDINGS,
          paddingVertical: DESKTOP_GAPS_AND_PADDINGS,
        }),
  }
})

const BannerContainer = styled.View(({ theme }) => ({
  paddingHorizontal: theme.contentPage.marginHorizontal,
  marginBottom: theme.designSystem.size.spacing.xl,
}))

const Container = styled.View(({ theme }) => ({
  marginBottom: theme.designSystem.size.spacing.xl,
}))

const StyledCategoryButton = styled(CategoryButton)<{ mobileMinWidth: string }>(
  ({ theme, mobileMinWidth }) => ({
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    minWidth: theme.isMobileViewport ? mobileMinWidth : DESKTOP_MIN_WIDTH,
    maxWidth: theme.isMobileViewport ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH,
  })
)
